import Papa from "papaparse";
import {
  ActuatorInterval,
  ActuatorKind,
  ProcessedTestData,
  SensorData,
} from "./types";

export const RESISTANCE_THRESHOLD = 2e12;
export const HIGH_RH_THRESHOLD = 93;

export const ACTUATOR_GPIO_COLUMNS: Record<ActuatorKind, string> = {
  soilHeater: "gpio.soil_heater",
  saltHeater: "gpio.salt_heater",
  saltPump0: "gpio.salt_pump0",
  saltPump1: "gpio.salt_pump1",
};

// GPIO values arrive as booleans (dynamicTyping) or "true"/"false" strings.
const isGpioOn = (value: unknown): boolean =>
  value === true || String(value).trim().toLowerCase() === "true";

/**
 * Derive the on-intervals of each TRO actuator (heaters/pumps) from the raw
 * CSV rows. Intervals are emitted in chart x-axis units: the charts drop the
 * first row and then use the array index as x, so row i maps to x = i - 1.
 */
export const extractActuatorIntervals = (
  rows: Array<Record<string, unknown>>
): ActuatorInterval[] => {
  const intervals: ActuatorInterval[] = [];

  // Zero-width intervals are dropped: a pulse confined to the first CSV row
  // (a common boot artifact) describes data the charts never display, and
  // would still surface a phantom legend chip.
  const pushInterval = (
    kind: ActuatorKind,
    onSinceRow: number,
    offRow: number
  ) => {
    const from = Math.max(0, onSinceRow - 1);
    const to = Math.max(0, offRow - 1);
    if (to > from) {
      intervals.push({ kind, from, to });
    }
  };

  (Object.keys(ACTUATOR_GPIO_COLUMNS) as ActuatorKind[]).forEach((kind) => {
    const column = ACTUATOR_GPIO_COLUMNS[kind];
    let onSince: number | null = null;

    rows.forEach((row, rowIndex) => {
      const on = isGpioOn(row[column]);
      if (on && onSince === null) {
        onSince = rowIndex;
      } else if (!on && onSince !== null) {
        pushInterval(kind, onSince, rowIndex);
        onSince = null;
      }
    });

    if (onSince !== null) {
      pushInterval(kind, onSince, rows.length - 1);
    }
  });

  return intervals.sort((a, b) => a.from - b.from);
};

export const processCsvData = async (
  csvUrl: string
): Promise<ProcessedTestData> => {
  return new Promise((resolve, reject) => {
    Papa.parse(csvUrl, {
      download: true,
      header: true,
      dynamicTyping: true,
      skipEmptyLines: true,
      complete: (result) => {
        const rows = result.data as any[];
        const processedData: ProcessedTestData = {
          resistance: [],
          capacitance: [],
          trh: [],
          accelerometer: [],
        };

        rows.forEach((row) => {
          const timestamp = row["timestamp"] || "";

          // Process resistance data
          const resistanceValues = Array.from({ length: 6 }, (_, i) =>
            Number(row[`rSensor.Sensor${i + 1}`])
          );
          processedData.resistance.push({
            timestamp,
            values: resistanceValues,
          });

          // Process capacitance data
          const capacitanceValues = Array.from({ length: 6 }, (_, i) =>
            Number(row[`cSensor.Sensor${i + 1}`])
          );
          processedData.capacitance.push({
            timestamp,
            values: capacitanceValues,
          });

          // Process TRH data
          processedData.trh.push({
            timestamp,
            cassetteTemp: Number(row["cassette.t"]),
            cassetteRH: Number(row["cassette.rh"]),
            internalTemp: Number(row["internal.t"]),
            internalRH: Number(row["internal.rh"]),
            externalTemp: Number(row["external.t"]),
            externalRH: Number(row["external.rh"]),
          });

          // Process accelerometer data
          const x = Number(row["accelerometer.x"]);
          const y = Number(row["accelerometer.y"]);
          const z = Number(row["accelerometer.z"]);

          if (!Number.isNaN(x) || !Number.isNaN(y) || !Number.isNaN(z)) {
            processedData.accelerometer.push({
              timestamp,
              x: Number.isNaN(x) ? 0 : x,
              y: Number.isNaN(y) ? 0 : y,
              z: Number.isNaN(z) ? 0 : z,
            });
          }
        });

        // Check for extremely high resistance
        processedData.hasE12Error = processedData.resistance.some((reading) =>
          reading.values.some((value) => value > RESISTANCE_THRESHOLD)
        );

        // Check for high humidity in first 10 readings
        processedData.hasHighRH = processedData.trh
          .slice(0, 10)
          .some((reading) => reading.cassetteRH > HIGH_RH_THRESHOLD);

        processedData.actuators = extractActuatorIntervals(rows);

        resolve(processedData);
      },
      error: (error) => {
        reject(error);
      },
    });
  });
};

export const formatNumber = {
  exponential: (value: number, precision: number = 0) =>
    value.toExponential(precision),
  fixed: (value: number, precision: number = 2) => value.toFixed(precision),
  acceleration: (value: number) => `${value.toFixed(3)} g`,
};

export const safeNumber = (value: number, fallback: number = 0): number => {
  return Number.isNaN(value) ? fallback : value;
};

export const createChartPoint = (x: number, y: number) => ({ x, y });

export const skipFirstPoint = <T>(data: T[]): T[] => data.slice(1);
